import { getMediaCacheData } from 'api/ugc';

/**
 * @param {Object} playedCounts - 目前已經播放顯示過的圖片數量分佈
 * @param {Object} totalCounts - 後端那邊圖片快取池的數量分佈
 * @param {Object} promoUUID - Call API 用的
 * @param {Object} dataLength - 想要請求幾張圖片，會依據目前已播放顯示過的數量分佈和後端的快取池數量分佈做對照，達成請求相對應數量分佈的圖片資源
 *
 * @returns {Object} - 回傳一個 promise，resolve 後 值為以下
 * {
 *     // 回傳請求來的圖片相關資料
 *     data: {
 *          first (Array): [
 *              {
 *                  serialNo (Number): xxx,
 *                  type (String): 'image' || 'video',
 *                  value (String): ${URL}
 *              },
 *              ...
 *          ],
 *          default (Array): [
 *              {
 *                  serialNo (Number): xxx,
 *                  type (String): 'image' || 'video',
 *                  value (String): ${URL}
 *              },
 *              ...
 *          ],
 *          totalLength: 總共有的資料長度 (上面兩個陣列長度的總和)，通常會和請求的 dataLength 一樣，除非原本快取池的數量就小於 dataLength
 *     },
 *     // 回傳下一次的已經顯示播放過的數量分佈
 *     nextPlayedCounts (Object): {
 *          first (Number): xxx,
 *          default (Number): xxx
 *     }
 * }
 *
 */
export default function getCurrentMediaCacheData({
    playedCounts = { first: 0, default: 0 },
    totalCounts = { first: 0, default: 0 },
    promoUUID,
    dataLength
} = {}) {
    return new Promise(async (resolve, reject) => {
        const pFirst = playedCounts.first;
        const pDefault = playedCounts.default;
        const tFirst = totalCounts.first;
        const tDefault = totalCounts.default;
        const pTotal = pFirst + pDefault;
        const tTotal = tFirst + tDefault;
        // 當累積的已播放總數加上現在要播放的數量大於快取總數的話 (pTotal + dataLength > tTotal)，則代表需要重置累積的已播放數量 重置為 0
        const initialFirstValue = tTotal - pTotal >= dataLength ? pFirst : 0;
        // 和 initialFirstValue 一樣判斷是否需要重置
        const initialDefaultValue = tTotal - pTotal >= dataLength ? pDefault : 0;
        // 計算快取的優先數量和累積的已播放優先數量加上該次請求數量的差值
        const firstDiffValue = tFirst - (initialFirstValue + dataLength);
        // 在該次播放結束後，已經播放的圖片數量總數和比例
        const nextPlayedCounts = { first: 0, default: 0 };
        // 該次播放的數量比例
        const playingCounts = { first: 0, default: 0 };
        // 如果快取總數只夠播放 1 次的話
        // 快取總數: 23, dataLength: 12
        // 23 / 12 = 1.966...，取商為只能播放 1 次
        const isOnlyEnoughOnce = ~~(tTotal / dataLength) < 2;

        // 將快取圖片全部播完一遍 稱為一個完整的輪播生命週期
        // 一個完整的輪播生命週期是由數個播放組成

        // 快取總數小於等於請求圖片數時，會進這條件幾乎可以肯定 dataLength 為 3
        // 因為 dataLength 就是顯示的圖片數它和 Layout 強關聯
        // 所以在處理 layout 時就會先判斷一次了，如果快取總數小於欲顯示圖片數的話則 layout 自動變為 3 塊的版型
        // 因此這邊如果快取總數又小於 dataLength 的話，可以斷定此時 dataLength 為 3
        if (tTotal <= dataLength) {
            // 把快取的全部圖片都播放出來
            nextPlayedCounts.first = tFirst;
            nextPlayedCounts.default = tDefault;

            playingCounts.first = tFirst;
            playingCounts.default = tDefault;
        } else if (isOnlyEnoughOnce) {
            // 此時依照優先順序去分配請求的圖片比例和數量
            nextPlayedCounts.first = initialFirstValue + dataLength + firstDiffValue;
            nextPlayedCounts.default = initialDefaultValue - firstDiffValue;

            playingCounts.first = nextPlayedCounts.first;
            playingCounts.default = nextPlayedCounts.default;
        } else {
            // 可以在輪播生命週期中播放 1 次以上的情況
            // 快取總數: 23, dataLength: 10
            // 23 / 10 = 2.3，取商為可以播放 2 次

            // 兩種情況分別為
            // firstDiffValue >= 0 代表快取的優先數量還能夠應付該次請求數量
            // firstDiffValue < 0 代表快取的優先數量不足以應付該次請求數量，可能會是只拿到部分優先或是全都拿預設
            if (firstDiffValue >= 0) {
                // 該次請求全拿優先的，預設皆不拿
                nextPlayedCounts.first = initialFirstValue + dataLength;
                nextPlayedCounts.default = initialDefaultValue;

                playingCounts.first = nextPlayedCounts.first - initialFirstValue;
                playingCounts.default = nextPlayedCounts.default - initialDefaultValue;
            } else {
                // 拿取部分優先或是都不拿優先，因應目前已播放的優先數量加上該次請求數量的加總 和快取優先數量的差值
                // 剩下不夠的差值都去和預設拿，不會有預設不夠的情況，因為在上面都判斷掉了，如果不夠便會重置已播放數量
                nextPlayedCounts.first = initialFirstValue + dataLength + firstDiffValue;
                nextPlayedCounts.default = initialDefaultValue - firstDiffValue;

                playingCounts.first = nextPlayedCounts.first - initialFirstValue;
                playingCounts.default = nextPlayedCounts.default - initialDefaultValue;
            }
        }

        try {
            const { payload = [] } =
                (await getMediaCacheData({
                    promoUUID,
                    firstNum: playingCounts.first,
                    defaultNum: playingCounts.default
                })) || {};
            const { first: firstMediaDatas = [], default: defaultMediaDatas = [] } =
                payload[0] || {};

            resolve({
                data: {
                    first: firstMediaDatas,
                    default: defaultMediaDatas,
                    totalLength: firstMediaDatas.length + defaultMediaDatas.length
                },
                nextPlayedCounts: {
                    ...nextPlayedCounts
                }
            });
        } catch (error) {
            console.log(error);
            reject(error);
        }
    });
}
